<script setup>
import { computed } from 'vue'
import Icon from '@/Components/UI/Icon.vue'

const props = defineProps({
  variant: { type: String, default: 'primary' }, // primary|secondary|ghost|danger
  size:    { type: String, default: 'md' },      // sm|md|lg
  loading: { type: Boolean, default: false },
  disabled:{ type: Boolean, default: false },
  iconLeft:  { type: String, default: '' },
  iconRight: { type: String, default: '' },
  type:    { type: String, default: 'button' },
  ariaLabel: { type: String, default: '' },
})
defineEmits(['click'])

const base = 'inline-flex items-center justify-center gap-1.5 font-medium select-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'

const variants = {
  primary:   'text-[color:var(--accentInk)] bg-[color:var(--accent)] hover:brightness-95 active:brightness-90 focus-visible:outline-[color:var(--accent)]',
  secondary: 'text-[color:var(--ink)] bg-[color:var(--surface)] border border-[color:var(--line)] hover:bg-[color:var(--surfaceAlt)] focus-visible:outline-[color:var(--accent)]',
  ghost:     'text-[color:var(--ink)] bg-transparent hover:bg-[color:var(--accentSoft)] focus-visible:outline-[color:var(--accent)]',
  danger:    'text-white bg-[color:var(--danger)] hover:brightness-95 active:brightness-90 focus-visible:outline-[color:var(--danger)]',
}

const sizes = {
  sm: 'h-7 px-2.5 text-xs rounded-[var(--radius-button)]',
  md: 'h-9 px-3 text-sm rounded-[var(--radius-button)]',
  lg: 'h-11 px-4 text-base rounded-[var(--radius-button)]',
}

const classes = computed(() => ['ui-btn', base, variants[props.variant], sizes[props.size]])
const iconSize = computed(() => (props.size === 'lg' ? 20 : 16))
</script>

<template>
  <button
    :type="type"
    :class="classes"
    :disabled="disabled || loading"
    :aria-label="ariaLabel || undefined"
    :aria-busy="loading || undefined"
    @click="$emit('click', $event)"
  >
    <Icon
      v-if="loading"
      name="refresh"
      :size="iconSize"
      class="animate-spin"
    />
    <Icon
      v-else-if="iconLeft"
      :name="iconLeft"
      :size="iconSize"
    />
    <slot />
    <Icon
      v-if="iconRight && !loading"
      :name="iconRight"
      :size="iconSize"
    />
  </button>
</template>
